import { Refresh } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Grid, IconButton, Stack, LinearProgress, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  getPresignedURLForGet,
  getRecentExcelUploads,
  IExcelImportHistory,
  IFileInfoDTO,
  sendEmailNotification,
} from 'wisestep-api';
import { WsDataTable, WsTitleBanner, Spinner } from 'wisestep-base';

import { success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

const CandidateUploadHistory = () => {
  const navigate = useNavigate();

  const [importHistory, setImportHistory] = useState<IExcelImportHistory[]>([]);
  const [firstRowProgress, setProgress] = useState<number>(0);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [loadHistoryData, setLoadHistoryData] = useState<boolean>(true);
  const retryCountRef = useRef<number>(retryCount);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [isNewUpload, setIsNewUpload] = useState<boolean>(false);

  const openPipeline = async (cell: Cell) => {
    const row = cell.row.original as IExcelImportHistory;
    if (row.fileId) navigate(`/candidates/mappings?fileId=${row.fileId ?? ''}`);
  };

  useEffect(() => {
    const MAX_RETRY_COUNT = 5;
    let progressUpdateTimeout;
    const fetchLatestProgress = async () => {
      try {
        setRetryCount(retryCountRef.current);

        const response = await getRecentExcelUploads();
        setImportHistory(response.data || []);

        const latestProgress: IExcelImportHistory = response.data[0];
        const calculatedProgress = calculateProgress(latestProgress);
        setProgress(calculatedProgress);

        // if calculated progress is not 100 and status is not completed and try is first then add a state vriable that isNewUpload = true (default false)
        if (
          calculatedProgress != 100 &&
          retryCountRef.current === 0 &&
          latestProgress.status !== 'COMPLETED'
        ) {
          setIsNewUpload(true);
        }

        if (isNewUpload && latestProgress.status === 'COMPLETED') {
          clearTimeout(progressUpdateTimeout);
          const createdByEmail = latestProgress.createdByEmail || '';
          const fileName = latestProgress.fileName || '';

          setNotification(success('Candidate Upload Completed'));
          try {
            await sendEmailNotification(createdByEmail, fileName);
          } catch (error) {
            console.error('Error sending email notification:', error);
          }
          setIsNewUpload(false);
        } else if (!isNewUpload && retryCountRef.current === 1) {
          // if we load with status completed, dont check more than once.
          clearTimeout(progressUpdateTimeout);
        } else if (
          latestProgress.status !== 'COMPLETED' &&
          retryCountRef.current <= MAX_RETRY_COUNT
        ) {
          retryCountRef.current++;
          progressUpdateTimeout = setTimeout(fetchLatestProgress, 50000);
        } else {
          // Handle the case where progress is stuck after reaching the maximum retry count
          clearTimeout(progressUpdateTimeout);
        }

        // Update import history
        setImportHistory((prevImportHistory) => {
          if (prevImportHistory && prevImportHistory.length > 0) {
            prevImportHistory[0] = latestProgress;
            return [...prevImportHistory];
          }
          return [latestProgress];
        });
      } catch (error) {
        console.log(error);
      }
    };
    // Set the initial timeout
    progressUpdateTimeout = setTimeout(fetchLatestProgress, 10000);

    // Clean up the timeout when the component unmounts
    return () => {
      clearTimeout(progressUpdateTimeout);
    };
  }, [isNewUpload, retryCount, setNotification]);

  useEffect(() => {
    const fetchData = async () => {
      if (loadHistoryData) {
        try {
          const response = await getRecentExcelUploads();
          setImportHistory(response.data || []);
          setLoadHistoryData(false);
        } catch (error) {
          console.log(error);
        }
      }
    };

    fetchData();
  }, [loadHistoryData]);

  function calculateProgress(row: IExcelImportHistory): number {
    const processed = row.processed || 0;
    const invalid = row.invalid || 0;
    const requested = row.requested || 0;

    if (requested === 0 || processed === 0) {
      return 0;
    }
    // Plus one in the case of header processing *
    const progressRatio = (processed + invalid + 1) / requested;
    return Math.floor(progressRatio * 100);
  }

  const columns = [
    {
      Header: 'File Name',
      accessor: 'fileName',
    },
    {
      Header: 'Batch Code',
      accessor: 'fileId',
      id: '1',
    },

    {
      Header: 'Upload Time',
      accessor: 'createdOn',
      id: '3',
      Cell: (cell: Cell) => {
        const row = cell.row.original as IExcelImportHistory;
        const uploadTime = row.createdOn;
        if (uploadTime) {
          const formattedDate = new Date(uploadTime).toLocaleString();
          return <div>{formattedDate}</div>;
        }
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      id: '6',
      Cell: (cell: Cell) => {
        const row = cell.row.original as IExcelImportHistory;
        let progress = 0;
        if (row.status != 'COMPLETED') {
          if (cell.row.index != 0) {
            progress = calculateProgress(row);
          } else {
            progress = firstRowProgress;
          }
          return (
            <Stack direction="column" alignItems="center" spacing={1}>
              <LinearProgress
                variant="determinate"
                sx={{
                  width: '100%',
                  height: 10,
                  borderRadius: 4,
                }}
                value={progress}
              />
              <div>{progress}</div>
            </Stack>
          );
        } else {
          return (
            <Stack direction="row" alignItems="center">
              <CheckCircleIcon style={{ color: 'green', marginLeft: '4px' }} />
            </Stack>
          );
        }
      },
    },
    {
      Header: 'Status Logs',
      accessor: 'progress',
      id: '5',
      Cell: (cell: Cell) => {
        const row = cell.row.original as IExcelImportHistory;
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title={'View In Pipeline'}>
              <IconButton onClick={() => openPipeline(cell)}>
                <OpenInNewIcon color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>
            {row.errorFileUrl ? (
              <Tooltip title={'Download Error Logs'}>
                <IconButton onClick={() => handleDownloadLogs(row)}>
                  <CloudDownloadIcon color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        );
      },
    },
  ];

  const handleDownloadLogs = async (row: IExcelImportHistory) => {
    try {
      const request: IFileInfoDTO = {
        fileName: row.errorFileUrl?.split('?')[0] ?? '',
        fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        downloadAs: 'Upload_Issues',
      };

      const response = await getPresignedURLForGet(request);
      if (response?.data) {
        window.open(response.data.preSignedUrl, '_blank');
      } else {
        console.error('Failed to fetch pre-signed URL.');
      }
    } catch (error) {
      console.error('Error while fetching pre-signed URL:', error);
    }
  };

  const handleRefreshClick = () => {
    setLoadHistoryData(true);
  };

  const customData = useMemo(
    () =>
      importHistory?.map((importHistoryObj) => ({
        ...importHistoryObj,
        progress: status,
      })),
    [importHistory]
  );

  return (
    <Grid>
      <WsTitleBanner
        title="Import History"
        goBack={() => navigate('/import-candidates/bulk-upload-excel')}
        rightChildren={
          <IconButton color="inherit" onClick={handleRefreshClick}>
            <Refresh style={{ color: 'white' }} />
          </IconButton>
        }
      />
      <Grid item xs={12} container justifyContent="flex-start" pt={2}>
        {!isEmpty(customData) ? (
          <WsDataTable
            columns={columns}
            data={customData ?? []}
            totalDataItems={customData?.length}
            selectable={false}
            boldItems={['batchCode']}
          />
        ) : (
          <Spinner />
        )}
      </Grid>
    </Grid>
  );
};

export default CandidateUploadHistory;
